import * as THREE from 'three';
import { generateFramedWall } from '../../domain/framing/generateFramedWall';
import type { GlazingZone, WallOpening } from '../../domain/framing/generateFramedWall';
import {
    ENGINE_CC_M as CC,
} from '../constants';
import { addBox, addSlopedBox } from '../geometry';
import { matPanel, matWood } from '../materials';
import { addSubfloorBays, resolveFloorJoistLayout } from '../subfloor';
import type {
    FloorBuildOptions,
    SquareBaseBuildContext,
    SquareBaseWall,
    WallFramingBuildOptions,
    WallFramingResult,
} from './context';
import {
    addProjectedWallBox,
    groupOpeningsByWall,
    projectWallPlacement,
    wallHeightAtAxis,
} from './context';

export function buildFloorJoists(
    ctx: SquareBaseBuildContext,
    framingGroup: THREE.Group,
    panelGroup: THREE.Group,
    options: FloorBuildOptions,
) {
    const { numFloorJoists, floorSpacing } = resolveFloorJoistLayout(ctx.W, ctx.fW, CC);
    for (let index = 0; index < numFloorJoists; index += 1) {
        const joist = addBox(
            ctx.fW,
            ctx.fH,
            ctx.L,
            matWood,
            -ctx.W / 2 + ctx.fW / 2 + index * floorSpacing,
            ctx.fH / 2,
            0,
            framingGroup,
        );
        joist.name = `detail/floor-joist/${options.namePrefix}-${index + 1}`;
    }

    const floorPanel = addBox(ctx.W, ctx.pT, ctx.L, matPanel, 0, ctx.fH + ctx.pT / 2, 0, panelGroup);
    floorPanel.name = `detail/floor-panel/${options.namePrefix}-main`;

    addSubfloorBays({
        width: ctx.W,
        length: ctx.L,
        joistWidth: ctx.fW,
        joistHeight: ctx.fH,
        cc: CC,
        group: framingGroup,
        namePrefix: options.namePrefix,
    });
}

function renderWallFraming(
    ctx: SquareBaseBuildContext,
    wall: SquareBaseWall,
    wallLengthMm: number,
    wallOpenings: WallOpening[],
    glazingZones: GlazingZone[],
    group: THREE.Group,
    options: WallFramingBuildOptions,
): WallFramingResult {
    const isGableEndWall = ctx.roofConfig.kind === 'gable' && (wall === 'front' || wall === 'back');
    const framingHeightMm = isGableEndWall
        ? Math.round(ctx.H * 1000)
        : Math.round(ctx.roofGeometry.getMaxWallHeight(wall) * 1000);

    let framed;
    try {
        framed = generateFramedWall({
            wallLengthMm,
            wallHeightMm: framingHeightMm,
            openings: wallOpenings,
            glazingZones,
        });
    } catch {
        framed = generateFramedWall({
            wallLengthMm,
            wallHeightMm: framingHeightMm,
            glazingZones,
        });
    }

    const studAxisPositions: number[] = [];

    for (const stud of framed.studs) {
        const axisCenterM = stud.xMm / 1000;
        const yStartM = stud.yStartMm / 1000;
        const yEndM = stud.yEndMm / 1000;
        let ySizeM = yEndM - yStartM;
        if (ySizeM <= 0) continue;

        const maxStudTopM = isGableEndWall ? ctx.H - ctx.sW : wallHeightAtAxis(ctx, wall, axisCenterM) - ctx.sW;
        const clampedEndM = Math.min(yEndM, maxStudTopM);
        ySizeM = clampedEndM - yStartM;

        if (ySizeM <= 0) continue;
        studAxisPositions.push(axisCenterM);

        addProjectedWallBox(
            ctx,
            wall,
            {
                axisCenterM,
                axisSizeM: ctx.sW,
                yCenterM: ctx.wallBase + yStartM + ySizeM / 2,
                ySizeM,
                normalDepthM: ctx.sD,
            },
            matWood,
            group,
            `detail/framing/${wall}-stud-${stud.type}-${options.namePrefix}-${stud.xMm}-${stud.yStartMm}`,
        );
    }

    for (const header of framed.headers) {
        const axisStartM = header.xStartMm / 1000;
        const axisEndM = header.xEndMm / 1000;
        // Invalid openings are reported by the shared buildability gate. Never
        // conceal an impossible opening by shrinking its structural header.
        const headerYStartM = header.yMm / 1000;
        const headerYEndM = headerYStartM + header.depthMm / 1000;
        if (headerYEndM <= headerYStartM) continue;
        addProjectedWallBox(
            ctx,
            wall,
            {
                axisCenterM: (axisStartM + axisEndM) / 2,
                axisSizeM: axisEndM - axisStartM,
                yCenterM: ctx.wallBase + (headerYStartM + headerYEndM) / 2,
                ySizeM: headerYEndM - headerYStartM,
                // A header is purchased as one 45 mm member, not the full 95 mm wall depth.
                normalDepthM: ctx.sW,
            },
            matWood,
            group,
            `detail/framing/${wall}-header-${header.openingId ?? options.namePrefix}`,
        );
    }

    for (const sill of framed.sills) {
        // The sill (bröstningsregel) butts BETWEEN the jack studs with its
        // top edge at the opening bottom — centred on the opening line it
        // straddles the window and interpenetrates both jacks.
        const axisStartM = sill.xStartMm / 1000 + ctx.sW / 2;
        const axisEndM = sill.xEndMm / 1000 - ctx.sW / 2;
        if (axisEndM <= axisStartM) continue;
        addProjectedWallBox(
            ctx,
            wall,
            {
                axisCenterM: (axisStartM + axisEndM) / 2,
                axisSizeM: axisEndM - axisStartM,
                yCenterM: ctx.wallBase + sill.yMm / 1000 - ctx.sW / 2,
                ySizeM: ctx.sW,
                normalDepthM: ctx.sD,
            },
            matWood,
            group,
            `detail/framing/${wall}-sill-${sill.openingId ?? options.namePrefix}`,
        );
    }

    return {
        wallOpenings,
        surfaceOpenings: [
            ...wallOpenings,
            ...glazingZones.flatMap((zone, index) => createGlazingSurfaceOpenings(wall, zone, wallOpenings, index)),
        ],
        studAxisPositions,
        horizontalMembers: [
            ...framed.headers.map((header) => ({
                axisStart: header.xStartMm / 1000,
                axisEnd: header.xEndMm / 1000,
                yStart: header.yMm / 1000,
                yEnd: (header.yMm + header.depthMm) / 1000,
            })),
            ...framed.sills.map((sill) => ({
                axisStart: sill.xStartMm / 1000,
                axisEnd: sill.xEndMm / 1000,
                // Mirrors the rendered sill: top edge at the opening bottom.
                yStart: sill.yMm / 1000 - ctx.sW,
                yEnd: sill.yMm / 1000,
            })),
        ],
    };
}

function createGlazingSurfaceOpenings(
    wall: SquareBaseWall,
    zone: GlazingZone,
    wallOpenings: WallOpening[],
    zoneIndex: number,
): WallOpening[] {
    const overlapping = wallOpenings
        .filter((opening) => {
            const start = opening.xMm;
            const end = opening.xMm + opening.widthMm;
            return start < zone.xEndMm && end > zone.xStartMm;
        })
        .sort((a, b) => a.xMm - b.xMm);
    const surfaceOpenings: WallOpening[] = [];
    let cursor = zone.xStartMm;

    for (const opening of overlapping) {
        const openingStart = Math.max(zone.xStartMm, opening.xMm);
        const openingEnd = Math.min(zone.xEndMm, opening.xMm + opening.widthMm);
        if (openingStart - cursor > 10) {
            surfaceOpenings.push(createGlazingSurfaceOpening(wall, zone, zoneIndex, surfaceOpenings.length, cursor, openingStart));
        }
        cursor = Math.max(cursor, openingEnd);
    }

    if (zone.xEndMm - cursor > 10) {
        surfaceOpenings.push(createGlazingSurfaceOpening(wall, zone, zoneIndex, surfaceOpenings.length, cursor, zone.xEndMm));
    }

    return surfaceOpenings;
}

function createGlazingSurfaceOpening(
    wall: SquareBaseWall,
    zone: GlazingZone,
    zoneIndex: number,
    segmentIndex: number,
    xStartMm: number,
    xEndMm: number,
): WallOpening {
    return {
        id: `${zone.id}-${zoneIndex + 1}-${segmentIndex + 1}`,
        wall,
        type: 'window',
        xMm: Math.round(xStartMm),
        yMm: zone.yMm,
        widthMm: Math.round(xEndMm - xStartMm),
        heightMm: zone.heightMm,
        glazed: true,
    };
}

/**
 * Hammarband on a front/back wall. Level at the wall's own (constant) height,
 * unless a mono roof falls along X (high wall left/right): then the front and
 * back are the RAKED walls and the plate is sheared along X, its centre on the
 * roof line at mid-wall so both ends land on the wall-line heights.
 */
function addEndWallTopPlate(ctx: SquareBaseBuildContext, wall: 'front' | 'back', group: THREE.Group) {
    const slope = ctx.roofGeometry.monoSlope;
    const name = `detail/framing/${wall}-top-plate`;
    if (slope?.axis === 'x') {
        const placement = projectWallPlacement(ctx, wall, {
            axisCenterM: ctx.W / 2,
            axisSizeM: ctx.W,
            yCenterM: ctx.wallBase + ctx.roofGeometry.getWallHeightAt(wall, ctx.W / 2) - ctx.sW / 2,
            ySizeM: ctx.sW,
            normalDepthM: ctx.sD,
        });
        addSlopedBox(
            placement.size.x, placement.size.y, placement.size.z, matWood,
            placement.position.x, placement.position.y, placement.position.z,
            group, slope.signedPitchRad, 'x',
        ).name = name;
        return;
    }
    addProjectedWallBox(
        ctx,
        wall,
        {
            axisCenterM: ctx.W / 2,
            axisSizeM: ctx.W,
            yCenterM: ctx.wallBase + ctx.roofGeometry.getMinWallHeight(wall) - ctx.sW / 2,
            ySizeM: ctx.sW,
            normalDepthM: ctx.sD,
        },
        matWood,
        group,
        name,
    );
}

/**
 * Hammarband on a left/right wall: raked (sheared along Z) under the
 * historical front-high mono, level at the wall's own eave height when the
 * mono falls along X (it is then an eave wall) and under a gable.
 */
function addSideWallTopPlate(ctx: SquareBaseBuildContext, wall: 'left' | 'right', group: THREE.Group) {
    const raked = ctx.roofGeometry.monoSlope?.axis === 'z';
    const top = projectWallPlacement(ctx, wall, {
        axisCenterM: ctx.L / 2,
        axisSizeM: ctx.L - 2 * ctx.sD,
        yCenterM: ctx.wallBase
            + ctx.roofGeometry.getMinWallHeight(wall)
            - ctx.sW / 2
            + (raked
                ? ((ctx.L - 2 * ctx.sD) * Math.tan(ctx.pitchRad)) / 2
                : 0),
        ySizeM: ctx.sW,
        normalDepthM: ctx.sD,
    });
    const mesh = !raked
        ? addBox(top.size.x, top.size.y, top.size.z, matWood, top.position.x, top.position.y, top.position.z, group)
        : addSlopedBox(top.size.x, top.size.y, top.size.z, matWood, top.position.x, top.position.y, top.position.z, group, ctx.pitchRad);
    mesh.name = `detail/framing/${wall}-top-plate`;
}

export function buildWallFraming(
    ctx: SquareBaseBuildContext,
    openings: WallOpening[],
    framingGroup: THREE.Group,
    frontFramingGroup: THREE.Group,
    options: WallFramingBuildOptions,
) {
    const grouped = groupOpeningsByWall(openings);
    const glazingZonesByWall = options.glazingZonesByWall ?? {};

    addProjectedWallBox(
        ctx,
        'front',
        {
            axisCenterM: ctx.W / 2,
            axisSizeM: ctx.W,
            yCenterM: ctx.wallBase + ctx.sW / 2,
            ySizeM: ctx.sW,
            normalDepthM: ctx.sD,
        },
        matWood,
        frontFramingGroup,
        'detail/framing/front-sill',
    );
    addEndWallTopPlate(ctx, 'front', frontFramingGroup);
    addProjectedWallBox(
        ctx,
        'back',
        {
            axisCenterM: ctx.W / 2,
            axisSizeM: ctx.W,
            yCenterM: ctx.wallBase + ctx.sW / 2,
            ySizeM: ctx.sW,
            normalDepthM: ctx.sD,
        },
        matWood,
        framingGroup,
        'detail/framing/back-sill',
    );
    addEndWallTopPlate(ctx, 'back', framingGroup);
    addProjectedWallBox(
        ctx,
        'left',
        {
            axisCenterM: ctx.L / 2,
            axisSizeM: ctx.L - 2 * ctx.sD,
            yCenterM: ctx.wallBase + ctx.sW / 2,
            ySizeM: ctx.sW,
            normalDepthM: ctx.sD,
        },
        matWood,
        framingGroup,
        'detail/framing/left-sill',
    );
    addProjectedWallBox(
        ctx,
        'right',
        {
            axisCenterM: ctx.L / 2,
            axisSizeM: ctx.L - 2 * ctx.sD,
            yCenterM: ctx.wallBase + ctx.sW / 2,
            ySizeM: ctx.sW,
            normalDepthM: ctx.sD,
        },
        matWood,
        framingGroup,
        'detail/framing/right-sill',
    );

    addSideWallTopPlate(ctx, 'left', framingGroup);
    addSideWallTopPlate(ctx, 'right', framingGroup);

    const results = new Map<SquareBaseWall, WallFramingResult>();
    results.set('front', renderWallFraming(ctx, 'front', Math.round(ctx.W * 1000), grouped.get('front') ?? [], glazingZonesByWall.front ?? [], frontFramingGroup, options));
    results.set('back', renderWallFraming(ctx, 'back', Math.round(ctx.W * 1000), grouped.get('back') ?? [], glazingZonesByWall.back ?? [], framingGroup, options));
    results.set('left', renderWallFraming(ctx, 'left', Math.round(ctx.L * 1000), grouped.get('left') ?? [], glazingZonesByWall.left ?? [], framingGroup, options));
    results.set('right', renderWallFraming(ctx, 'right', Math.round(ctx.L * 1000), grouped.get('right') ?? [], glazingZonesByWall.right ?? [], framingGroup, options));
    return results;
}
